// inv/src/pages/payment/PlanCard.jsx
import { useTranslation } from 'react-i18next';

const getPlanConfigs = (variant = 'pricing', plans, selectedInterval, t) => {
  const periodLabels = {
    month: t('pagePricing.content.other.f1'),
    quarter: t('pagePricing.content.other.f2'),
    year: t('pagePricing.content.other.f3'),
  };

  return [
    {
      key: 'free',
      title: t('pagePricing.content.freeMembership.name'),
      price: '$0',
      description: t('pagePricing.content.freeMembership.description'),
      features: [
        t('pagePricing.content.features.f1'),
        t('pagePricing.content.features.f10'),
        t('pagePricing.content.features.f2'),
        t('pagePricing.content.features.f4'),
      ],
      periodLabel: '',
      backgroundClass: 'bg-custom1 shadow-md border',
      textClasses: {
        title: 'text-gray-950',
        price: 'text-gray-950',
        period: 'text-gray-950',
        description: 'text-gray-950',
        features: 'text-gray-950',
        icon: 'text-gray-950',
      },
      buttonClass:
        'cursor-pointer mt-8 w-full bg-stone-800 hover:bg-stone-700 text-white px-5 py-2.5 text-sm rounded-lg',
      planId: null,
    },
    {
      key: 'startup',
      title: plans.startup[selectedInterval]?.name || t('pagePricing.content.startupMembership.name'),
      price: plans.startup[selectedInterval]?.recurring_price
        ? `$${plans.startup[selectedInterval].recurring_price}`
        : '',
      description: t('pagePricing.content.startupMembership.description'),
      features: [
        t('pagePricing.content.features.f5'),
        t('pagePricing.content.features.f11'),
        t('pagePricing.content.features.f12'),
        t('pagePricing.content.features.f6'),
        t('pagePricing.content.features.f13'),
        t('pagePricing.content.features.f3'),
      ],
      periodLabel: periodLabels[selectedInterval],
      backgroundClass: 'bg-custom2 shadow-md border',
      textClasses: {
        title: 'text-white',
        price: 'text-white',
        period: 'text-slate-200',
        description: 'text-slate-200',
        features: 'text-slate-200',
        icon: 'text-slate-200',
      },
      buttonClass:
        'cursor-pointer mt-8 w-full bg-stone-800 hover:bg-stone-700 text-white px-5 py-2.5 text-sm rounded-lg',
      planId: plans.startup[selectedInterval]?.id,
    },
    {
      key: 'professional',
      title: plans.professional[selectedInterval]?.name || t('pagePricing.content.professionalMembership.name'),
      price: plans.professional[selectedInterval]?.recurring_price
        ? `$${plans.professional[selectedInterval].recurring_price}`
        : '',
      description: t('pagePricing.content.professionalMembership.description'),
      features: [
        t('pagePricing.content.features.f7'),
        t('pagePricing.content.features.f14'),
        t('pagePricing.content.features.f15'),
        t('pagePricing.content.features.f16'),
        t('pagePricing.content.features.f11'),
        t('pagePricing.content.features.f6'),
        t('pagePricing.content.features.f13'),
        t('pagePricing.content.features.f9'),
        t('pagePricing.content.features.f8'),
      ],
      periodLabel: periodLabels[selectedInterval],
      backgroundClass: 'bg-custom3 shadow-md border',
      textClasses: {
        title: 'text-white',
        price: 'text-white',
        period: 'text-slate-200',
        description: 'text-slate-200',
        features: 'text-slate-200',
        icon: 'text-slate-200',
      },
      buttonClass:
        'cursor-pointer mt-8 w-full bg-stone-800 hover:bg-stone-700 text-white px-5 py-2.5 text-sm rounded-lg',
      planId: plans.professional[selectedInterval]?.id,
    },
  ];
};

export default function PlanCard({
  planKey,
  title,
  price,
  description,
  backgroundClass,
  features,
  periodLabel,
  disabled,
  pending = false,
  buttonClass,
  onPlanSelect,
  config,
  isAuthenticated,
  textClasses = {},
}) {
  const { t } = useTranslation();
  const newFeatureLabels = new Set([
    t('pagePricing.content.features.f9'),
    t('pagePricing.content.features.f15'),
    t('pagePricing.content.features.f16'),
  ]);

  const {
    title: titleClass = 'text-gray-800',
    price: priceClass = 'text-gray-900',
    period: periodClass = 'text-gray-700',
    description: descClass = 'text-gray-700',
    features: featClass = 'text-gray-700',
    icon: iconClass = 'text-gray-700',
  } = textClasses;

  let buttonText;
  if (pending) {
    buttonText = t('pagePricing.content.other.fPending', 'Pending');
  } else if (disabled) {
    buttonText = t('pagePricing.content.other.f5');
  } else if (!isAuthenticated) {
    buttonText = t('pagePricing.content.signupButton');
  } else {
    buttonText = t('pagePricing.content.other.f4');
  }

  return (
    <div
      className={`flex flex-col rounded-lg p-6 relative isolate h-full ${backgroundClass}`}
    >
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex-1">
          <h3 className={`text-center text-lg font-bold ${titleClass}`}>{title}</h3>

          {planKey === 'free' ? (
            <div className="mt-6 text-center">
              <h2 className={`text-3xl font-bold ${priceClass}`}>{price}</h2>
            </div>
          ) : (
            <div className="mt-6 flex items-center w-full">
              <h2 className={`m-auto text-3xl font-bold ${priceClass}`}>{price}</h2>
              <h6 className={`m-auto text-sm font-bold ${periodClass}`}>{periodLabel}</h6>
            </div>
          )}

          <p className={`mt-4 text-sm border-b border-stone-400 pb-6 ${descClass}`}>{description}</p>

          <ul className={`mt-6 space-y-4 text-sm ${featClass}`}>
            {features.map((f, i) => {
              const showNew = newFeatureLabels.has(f);
              return (
                <li key={i} className="flex items-start justify-between gap-3">
                  <div className="flex items-start min-w-0 flex-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      className={`mr-2 mt-1 shrink-0 ${iconClass}`}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M9.707 19.121a.997.997 0 0 1-1.414 0l-5.646-5.647a1.5 1.5 0 0 1 0-2.121l.707-.707a1.5 1.5 0 0 1 2.121 0L9 14.171l9.525-9.525a1.5 1.5 0 0 1 2.121 0l.707.707a1.5 1.5 0 0 1 0 2.121z" />
                    </svg>
                    <span className="whitespace-normal break-words leading-snug">{f}</span>
                  </div>

                  {showNew && (
                    <span
                      className="ml-3 mt-0.5 inline-flex shrink-0 items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold
                       text-gray-800 border border-gray-200 shadow-sm"
                      aria-label="New feature"
                    >
                      NEW
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

        </div>

        <button
          type="button"
          className={`mt-8 ${buttonClass} ${disabled ? 'opacity-70 !cursor-not-allowed' : ''}`}
          disabled={disabled}
          onClick={() => onPlanSelect(config)}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}

export { getPlanConfigs };
